// === src/pages/dashboard/Settings.jsx ===
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import { deleteAccount } from "../../services/api";
import Loader from "../../components/Loader";

const Settings = () => {
  const { user, logout } = useAuth();
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("theme") === "dark");
  const [timezone, setTimezone] = useState("UTC");
  const [profileImage, setProfileImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(darkMode ? "dark" : "light");
    localStorage.setItem("theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  const handleSave = () => {
    if (!user) {
      toast.error("You must be logged in to save settings.");
      return;
    }

    if (!timezone) {
      toast.warning("Please select a valid timezone.");
      return;
    }

    setProcessing(true);
    setTimeout(() => {
      toast.success("Settings saved successfully.");
      setProcessing(false);
    }, 600);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      setProfileImage(URL.createObjectURL(file));
    } else {
      toast.error("Please upload a valid image file.");
    }
  };

  const handleAccountDelete = async () => {
    if (!user) {
      toast.error("No active session found.");
      return;
    }

    const password = prompt("Please enter your password to confirm deletion:");
    if (!password) return;

    const confirmed = window.confirm("Are you sure you want to delete your account? This action cannot be undone.");
    if (!confirmed) return;

    try {
      setProcessing(true);
      await deleteAccount(password);
      toast.success("Account deleted successfully.");
      localStorage.removeItem("token");
      logout();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data || "Failed to delete account.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="relative max-w-2xl mx-auto p-6 min-h-[600px]">
      {(loading || processing) && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-black/60 rounded">
          <Loader />
        </div>
      )}
      <div className={`${loading || processing ? "opacity-30 pointer-events-none" : ""}`}>
        <h1 className="text-2xl font-bold mb-6">Settings</h1>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-1">Profile Picture</label>
            <input type="file" accept="image/*" onChange={handleImageUpload} className="mb-2" />
            {profileImage && (
              <img src={profileImage} alt="Profile Preview" className="h-24 w-24 rounded-full object-cover" />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email Notifications</label>
            <input
              type="checkbox"
              checked={emailNotifications}
              onChange={(e) => setEmailNotifications(e.target.checked)}
              className="mr-2"
            />
            <span>{emailNotifications ? "Enabled" : "Disabled"}</span>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Dark Mode</label>
            <input
              type="checkbox"
              checked={darkMode}
              onChange={(e) => setDarkMode(e.target.checked)}
              className="mr-2"
            />
            <span>{darkMode ? "Enabled" : "Disabled"}</span>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="border px-3 py-2 rounded w-60 bg-white dark:bg-gray-800 text-black dark:text-white"
            >
              <option value="">Select timezone</option>
              <option value="UTC">UTC</option>
              <option value="EST">EST</option>
              <option value="PST">PST</option>
              <option value="CST">CST</option>
            </select>
          </div>
          <button
            onClick={handleSave}
            className="bg-orange-500 text-white px-6 py-2 rounded shadow mt-4"
          >
            Save Settings
          </button>
          <div className="mt-8 border-t pt-4">
            <h2 className="text-sm font-bold text-red-600 mb-2">Danger Zone</h2>
            <button
              onClick={handleAccountDelete}
              className="bg-red-600 text-white px-6 py-2 rounded shadow"
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;